import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { MARKETING } from "../../constants/testIds";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "../ui/sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { AdPreview } from "./AdPreview";
import { useAppData } from "../../contexts/AppDataContext";

const OBJECTIVES = [
  { value: "lead_generation", label: "Lead generation" },
  { value: "traffic", label: "Traffic to website" },
  { value: "conversions", label: "Conversions" },
  { value: "awareness", label: "Awareness & reach" },
];

const selectStyle = {
  border: "1px solid var(--scarlet-hairline)", background: "var(--scarlet-surface)", color: "var(--scarlet-text)",
};

function today(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

// Creating a campaign. A template pre-fills budget, dates, audience and creatives; everything
// stays editable afterwards because the values are COPIED onto the campaign, not referenced.
export function CampaignWizard({ open, template, onOpenChange }) {
  const queryClient = useQueryClient();
  const { me } = useAppData();
  const gymName = me?.organization_display_name || "Your Gym";
  const [form, setForm] = useState({
    name: "", platform: "meta", objective: "lead_generation", budget: "500",
    start_date: today(), end_date: today(30), branch_id: "", notes: "",
  });
  const [error, setError] = useState("");

  const branchesQ = useQuery({
    queryKey: ["branches-list"],
    queryFn: async () => (await api.get("/branches")).data.branches,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm((f) => ({
      ...f,
      name: template ? `${template.name} - ${new Date().toLocaleDateString("en-IN", { month: "short", year: "numeric" })}` : "",
      platform: template ? (template.platforms.length > 1 ? "both" : template.platforms[0]) : "meta",
      objective: template ? template.objective : "lead_generation",
      budget: template ? String(template.budget_min) : "500",
      start_date: today(),
      end_date: today(template ? template.duration_days : 30),
    }));
  }, [open, template]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const createMutation = useMutation({
    mutationFn: () => api.post("/marketing/campaigns", {
      name: form.name.trim(),
      platform: form.platform,
      objective: form.objective,
      // Rupees in the UI, paise on the wire — same convention as every other money field here.
      daily_budget: Math.round(Number(form.budget) * 100),
      start_date: form.start_date,
      end_date: form.end_date || undefined,
      branch_id: form.branch_id || undefined,
      template_id: template?.id,
      notes: form.notes || undefined,
    }),
    onSuccess: () => {
      toast.success("Campaign created as a draft.");
      queryClient.invalidateQueries({ queryKey: ["marketing-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["marketing-analytics"] });
      onOpenChange(false);
    },
    onError: (e) => setError(formatApiError(e).message),
  });

  const budgetNum = Number(form.budget);
  const valid = form.name.trim() && form.start_date && budgetNum > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal sm:rounded-[14px] max-h-[85vh] overflow-y-auto" data-testid={MARKETING.wizardDialog}>
        <DialogHeader>
          <DialogTitle>{template ? `New campaign from "${template.name}"` : "Create campaign"}</DialogTitle>
          <DialogDescription>
            {template
              ? "Budget, dates, audience and creatives are pre-filled from the template — adjust anything you like."
              : "It starts as a draft, so nothing goes live until you publish it."}
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm shake-on-error" style={{ color: "var(--scarlet-neg)" }}>{error}</p>}

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Campaign name</Label>
            <Input data-testid={MARKETING.wizardName} value={form.name} onChange={set("name")} placeholder="Free Trial - June" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Platform</Label>
              <select data-testid={MARKETING.wizardPlatform} value={form.platform} onChange={set("platform")}
                className="w-full h-10 rounded-[8px] px-3 text-sm" style={selectStyle}>
                <option value="meta">Meta (Facebook & Instagram)</option>
                <option value="google">Google Ads</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Objective</Label>
              <select data-testid={MARKETING.wizardObjective} value={form.objective} onChange={set("objective")}
                className="w-full h-10 rounded-[8px] px-3 text-sm" style={selectStyle}>
                {OBJECTIVES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Daily budget (₹)</Label>
              <Input data-testid={MARKETING.wizardBudget} type="number" min={1} value={form.budget} onChange={set("budget")} />
            </div>
            <div className="space-y-1.5">
              <Label>Start</Label>
              <Input data-testid={MARKETING.wizardStart} type="date" value={form.start_date} onChange={set("start_date")} />
            </div>
            <div className="space-y-1.5">
              <Label>End</Label>
              <Input data-testid={MARKETING.wizardEnd} type="date" value={form.end_date} onChange={set("end_date")} />
            </div>
          </div>

          {template && form.end_date && form.start_date && (
            <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>
              Roughly ₹{(budgetNum * Math.max(1, Math.round((new Date(form.end_date) - new Date(form.start_date)) / 86400000))).toLocaleString("en-IN")} total
              at this daily budget. Template suggests ₹{template.budget_min}–{template.budget_max}/day.
            </p>
          )}

          <div className="space-y-1.5">
            <Label>Branch <span className="text-xs font-normal" style={{ color: "var(--scarlet-text-secondary)" }}>(optional)</span></Label>
            <select value={form.branch_id} onChange={set("branch_id")} className="w-full h-10 rounded-[8px] px-3 text-sm" style={selectStyle}>
              <option value="">All branches</option>
              {(branchesQ.data || []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label>Notes <span className="text-xs font-normal" style={{ color: "var(--scarlet-text-secondary)" }}>(optional, internal)</span></Label>
            <Textarea rows={2} value={form.notes} onChange={set("notes")} placeholder="Anything worth remembering about this campaign…" />
          </div>

          {template && (
            <div className="space-y-2">
              <p className="text-sm font-semibold" style={{ color: "var(--scarlet-text)" }}>
                Preview — {template.creatives.length} ready-made creatives
              </p>
              <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>
                This is what will go out. You can change the wording after creating the draft.
              </p>
              <AdPreview creatives={template.creatives} platform={form.platform} gymName={gymName} />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            data-testid={MARKETING.wizardSubmit} disabled={!valid || createMutation.isPending}
            onClick={() => createMutation.mutate()} className="text-white" style={{ background: "var(--scarlet-accent)" }}
          >
            {createMutation.isPending ? "Creating…" : "Create draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
